import React, { useState, useCallback } from 'react';
import { toast } from 'react-hot-toast';
import TabHeader from '../../Components/TabHeader/TabHeader';
import { useClient } from '../../../../api/api-client';
import Button from '../../../../components/ComponentsV1/Button';
import { Star } from '../../../../UiKit/Star/Star';
import { SOP_ENDPOINTS, SOP_QUERY_PARAMS, SOP_STATUS, SKELETON_ROWS } from './constants';
import { Skeleton } from '../../../../components/ComponentsV1/Skeleton/Skeleton';
import SopResult from './Elements/SopResult';
import SopDropdown from './Elements/SopDropdown';

const ViewUpdateDeleteSops = () => {
  const client = useClient();

  // Step 1 — PID input
  const [pid, setPid] = useState('');
  const [isFetchingIssues, setIsFetchingIssues] = useState(false);

  // Step 2 — dropdowns populated after fetching issues
  const [issuesData, setIssuesData] = useState(null);
  const [selectedIssue, setSelectedIssue] = useState(null);
  const [selectedSubIssue, setSelectedSubIssue] = useState(null);
  const [dropdownResetKey, setDropdownResetKey] = useState(0);

  // Step 3 — SOP result
  const [sopStatus, setSopStatus] = useState(SOP_STATUS.NONE);
  const [sopData, setSopData] = useState(null);
  const [isFetchingSop, setIsFetchingSop] = useState(false);
  const [isActionLoading, setIsActionLoading] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [isUpdateMode, setIsUpdateMode] = useState(false);

  const resetDropdowns = useCallback(() => {
    setSelectedIssue(null);
    setSelectedSubIssue(null);
    setDropdownResetKey((k) => k + 1);
  }, []);

  const resetResult = useCallback(() => {
    setSopStatus(SOP_STATUS.NONE);
    setSopData(null);
    setShowDeleteConfirm(false);
    setIsUpdateMode(false);
  }, []);

  const resetAll = useCallback(() => {
    setIssuesData(null);
    resetDropdowns();
    resetResult();
  }, [resetDropdowns, resetResult]);

  // Step 1 — fetch issues by PID
  const handleFetchIssues = async (e) => {
    e.preventDefault();
    if (!pid.trim()) {
      toast.error('Product ID is required');
      return;
    }

    resetAll();
    setIsFetchingIssues(true);

    try {
      const response = await client(
        `${SOP_ENDPOINTS.FETCH_ISSUES}?${new URLSearchParams({ [SOP_QUERY_PARAMS.PID]: pid.trim() })}`,
      );

      const pidData = Array.isArray(response)
        ? response.find((item) => String(item.pid) === pid.trim())
        : null;

      if (!pidData || !pidData.issues?.length) {
        toast('No SOP exists for this PID');
        return;
      }

      setIssuesData(pidData.issues);
    } catch (error) {
      toast.error(
        error?.error_message || error?.message || error?.detail || error?.error || 'Failed to fetch issues for this PID',
      );
    } finally {
      setIsFetchingIssues(false);
    }
  };

  // Step 2 — view SOP using selected issue + sub-issue
  const handleViewSop = async (e) => {
    e.preventDefault();
    if (!selectedIssue || !selectedSubIssue) {
      toast.error('Please select both Issue Type and Sub-Issue Type');
      return;
    }

    resetResult();
    setIsFetchingSop(true);

    try {
      const params = `${SOP_QUERY_PARAMS.PID}=${encodeURIComponent(pid.trim())}&${SOP_QUERY_PARAMS.ISSUE}=${encodeURIComponent(selectedIssue.value)}&${SOP_QUERY_PARAMS.SUB_ISSUE}=${encodeURIComponent(selectedSubIssue.value)}`;
      const response = await client(`${SOP_ENDPOINTS.FETCH}?${params}`);

      if (response) {
        setSopData(response);
        setSopStatus(SOP_STATUS.FOUND);
        toast.success('SOP fetched successfully');
      } else {
        setSopStatus(SOP_STATUS.NOT_FOUND);
        toast('SOP does not exist for the given parameters.');
      }
    } catch (error) {
      const is404 =
        error?.status === 404 ||
        error?.code === 404 ||
        (error?.message || error?.detail || error?.error || '')
          .toLowerCase()
          .includes('sop not found');
      if (is404) {
        setSopStatus(SOP_STATUS.NOT_FOUND);
        toast('SOP does not exist for the given parameters.');
      } else {
        toast.error(
          error?.error_message || error?.message || error?.detail || error?.error || 'Failed to fetch SOP',
        );
      }
    } finally {
      setIsFetchingSop(false);
    }
  };

  const handleUpdate = async ({ modelName, troubleshootingSteps }) => {
    setIsActionLoading(true);
    try {
      await client(SOP_ENDPOINTS.CREATE_OR_UPDATE, {
        data: {
          pid: pid.trim(),
          issue_type: selectedIssue.value,
          sub_issue_type: selectedSubIssue.value,
          model_name: modelName,
          troubleshooting_steps: troubleshootingSteps,
        },
        method: 'POST',
      });
      toast.success('SOP updated successfully');
      setPid('');
      resetAll();
      resetResult();
    } catch (error) {
      toast.error(
        error?.error_message || error?.message || error?.detail || error?.error || 'Failed to update SOP',
      );
    } finally {
      setIsActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setIsActionLoading(true);
    try {
      const deleteParams = `${SOP_QUERY_PARAMS.PID}=${encodeURIComponent(pid.trim())}&${SOP_QUERY_PARAMS.ISSUE}=${encodeURIComponent(selectedIssue.value)}&${SOP_QUERY_PARAMS.SUB_ISSUE}=${encodeURIComponent(selectedSubIssue.value)}`;
      await client(`${SOP_ENDPOINTS.DELETE}?${deleteParams}`, { method: 'DELETE' });
      toast.success('SOP deleted successfully');
      setPid('');
      resetAll();
    } catch (error) {
      toast.error(
        error?.error_message || error?.message || error?.detail || error?.error || 'Failed to delete SOP',
      );
    } finally {
      setIsActionLoading(false);
      setShowDeleteConfirm(false);
    }
  };

  const issueOptions = (issuesData || []).map((item) => ({
    label: item.issue_type,
    value: item.issue_type,
  }));

  const subIssueOptions = selectedIssue
    ? (
        issuesData?.find((item) => item.issue_type === selectedIssue.value)
          ?.sub_issue_types || []
      ).map((s) => ({ label: s, value: s }))
    : [];

  const handleIssueChange = (val) => {
    setSelectedIssue(val);
    setSelectedSubIssue(null);
    setDropdownResetKey((k) => k + 1);
    resetResult();
  };

  return (
    <div className='tw-w-full tw-flex tw-flex-col tw-h-max tw-gap-10'>
      <div className='tw-w-full tw-flex tw-flex-col tw-h-max tw-gap-10 tw-bg-white tw-p-8 tw-rounded-2xl'>
        <TabHeader
          title='View, Update or Delete SOPs'
          subTitle='Select the details of the SOP to view it. You can then edit or delete the SOP.'
        />

        {/* Step 1 — PID */}
        <form onSubmit={handleFetchIssues}>
          <div className='tw-flex tw-flex-col tw-h-max tw-gap-5 tw-flex-1'>
            <div className='tw-w-full tw-text-grey-900 tw-font-medium tw-text-500'>
              Product ID (PID) <Star icon='*' />
            </div>
            <div className='tw-flex tw-flex-col tw-w-full'>
              <input
                id='pid'
                name='pid'
                placeholder='Enter product ID'
                value={pid}
                onChange={(e) => {
                  setPid(e.target.value);
                  resetAll();
                }}
                className='tw-appearance-none tw-px-5 tw-py-2 tw-h-16 tw-rounded-lg tw-outline-0 focus:tw-border-none focus:tw-ring-[1.5px] tw-w-full focus:tw-ring-base-green focus:tw-border-base-green tw-border tw-border-solid tw-border-[#cfcfcf]'
                autoComplete='off'
              />
            </div>
          </div>

          <div className='tw-mt-8'>
            <Button title='Fetch Issues' disabled={isFetchingIssues} />
          </div>
        </form>

        {isFetchingIssues && <Skeleton rows={SKELETON_ROWS} />}

        {/* Step 2 — Issue + Sub-Issue dropdowns */}
        {!isFetchingIssues && issuesData && (
          <form onSubmit={handleViewSop}>
            <div className='tw-flex tw-w-full tw-h-max tw-gap-5'>
              <SopDropdown
                key={`issue-${dropdownResetKey}`}
                title='Issue Type'
                placeholder='Select Issue Type'
                options={issueOptions}
                value={selectedIssue}
                onChange={handleIssueChange}
              />
              <SopDropdown
                key={`sub-issue-${dropdownResetKey}`}
                title='Sub-Issue Type'
                placeholder='Select Sub-Issue Type'
                options={subIssueOptions}
                value={selectedSubIssue}
                onChange={(val) => {
                  setSelectedSubIssue(val);
                  resetResult();
                }}
                disabled={!selectedIssue}
              />
            </div>

            <div className='tw-mt-8'>
              <Button
                title='View SOP'
                disabled={isFetchingSop || !selectedIssue || !selectedSubIssue}
              />
            </div>
          </form>
        )}
      </div>

      <SopResult
        sopStatus={sopStatus}
        sopData={sopData}
        isFetching={isFetchingSop}
        isActionLoading={isActionLoading}
        showDeleteConfirm={showDeleteConfirm}
        setShowDeleteConfirm={setShowDeleteConfirm}
        isUpdateMode={isUpdateMode}
        setIsUpdateMode={setIsUpdateMode}
        onUpdate={handleUpdate}
        onDelete={handleDelete}
        onCreate={null}
      />
    </div>
  );
};

export default ViewUpdateDeleteSops;
